<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import StemneButton from '@/components/ui/StemneButton.vue'

// The failed-load sibling of StemneEmptyState. When a fetch fails (404 / 500 /
// network) a section must NOT fall back to the "there is nothing here yet"
// empty state — an operator can't tell a broken load from genuinely-no-data,
// and every empty state quietly loses its meaning (#2663). This renders the
// failure explicitly: one muted line + a "prøv igjen" action.
//
// Empty vs failed — the convention:
//   • StemneEmptyState → the load SUCCEEDED and returned nothing. Flora, mood,
//     an invitation to add the first item.
//   • StemneLoadError  → the load FAILED. Muted and informational — never rust,
//     which is for destructive actions, not "we couldn't reach the server" —
//     with a retry. Wire @retry to the section's retry()/load().
// A composable's `status` (see useAsyncSection) is 'empty' vs 'failed'; switch
// the two components on it so they can never both show, and never swap.
//
// Two shapes, mirroring StemneEmptyState:
//   panel  (default) — the failure IS the section's content. Bordered card,
//                      title line, retry button below.
//   inline           — the section already has its own chrome (a banner above
//                      a dashboard, a card body). One muted line, retry beside
//                      it, no surface of its own.
defineProps<{
  // The failure line. Defaults to a generic "kunne ikke laste innholdet"; pass
  // the composable's resolved `error` for a resource-specific line.
  title?: string
  // Optional second sentence — rarely needed; the retry usually says enough.
  body?: string
  variant?: 'panel' | 'inline'
  // Show the retry button in its loading state while a retry is in flight —
  // bind to the section's `loading`.
  retrying?: boolean
  // Fixed-language marketing pages can override the global locale's label.
  retryLabel?: string
}>()

defineEmits<{ retry: [] }>()

const { t } = useI18n()
</script>

<template>
  <div class="load-error" :class="`load-error--${variant ?? 'panel'}`" role="alert">
    <div class="load-error__text">
      <div class="load-error__title">{{ title ?? t('loadError.title') }}</div>
      <p v-if="body" class="load-error__body">{{ body }}</p>
    </div>
    <StemneButton
      class="load-error__retry"
      :variant="(variant ?? 'panel') === 'inline' ? 'quiet' : 'secondary'"
      :loading="retrying"
      @click="$emit('retry')"
    >
      {{ retryLabel ?? t('loadError.retry') }}
    </StemneButton>
  </div>
</template>

<style lang="scss" scoped>
.load-error__title {
  font-family: var(--font-sans);
  font-size: var(--fs-body-md);
  color: var(--fg-2);
  line-height: 1.55;
}

.load-error__body {
  font-family: var(--font-sans);
  font-size: var(--fs-body-sm);
  color: var(--fg-muted);
  line-height: 1.5;
  margin: var(--space-1) 0 0;
}

// Panel: the failure IS the section. A quiet card — subtle border, not the
// dashed border + flora of an empty state (this isn't an invitation), not a
// rust wall (this isn't destructive).
.load-error--panel {
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: 14px;
  padding: 40px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--space-4);

  @media (max-width: 540px) {
    padding: 28px 20px;
  }
}

// Inline: no surface of its own. One muted line with the retry beside it,
// wrapping under it on narrow viewports.
.load-error--inline {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--space-2) var(--space-4);
}
</style>
